/**
 * Sessie 125: de koppelingscontrole per bron, voor het blok Koppelingen
 * op Gezondheid.
 *
 * Eigen leesweg (`GET /api/bewaking/koppelingen`, alleen beheerder),
 * zodat een trage of stukke bewaking de rest van de pagina laat staan.
 * Types staan hier en niet in `lib/werkplek-api.ts` (gedeeld). Het
 * contract staat in `docs/BOUWPLAN-sessie-125.md`. De opmaak (woord,
 * symbool, "3 min geleden") staat hier ook, zodat Koppelingen.tsx alleen
 * componenten exporteert.
 */
import { apiClient } from "@/lib/api";

import { dagTijd, klok } from "../../WerkplekV6/gedeeld/opmaak";

export type KoppelingStatus = "werkt" | "traag" | "uitgevallen" | "onbekend";

export interface KoppelingBron {
  /** Vaste id: `database`, `picqer`, `lightspeed`, ... */
  bron: string;
  /** Naam voor het scherm ("Picqer"). */
  naam: string;
  /** `onbekend` = nog nooit gecontroleerd of niet ingericht. */
  status: KoppelingStatus;
  laatste_controle_op: string | null;
  laatste_succes_op: string | null;
  laatste_fout_op: string | null;
  /** Korte zin in gewone taal; nooit een URL, sleutel of ruwe fout. */
  laatste_fout_oorzaak: string | null;
  responstijd_ms: number | null;
  traag_drempel_ms: number | null;
  mislukt_op_rij: number;
  uitgevallen_sinds: string | null;
  toelichting: string | null;
}

export interface KoppelingSamenvatting {
  werkt: number;
  traag: number;
  uitgevallen: number;
  onbekend: number;
}

export interface Koppelingen {
  bijgewerkt_op: string | null;
  samenvatting: KoppelingSamenvatting;
  bronnen: KoppelingBron[];
}

/** Sessie 141 B1: stand van een nachtronde of wachtrij. */
export type TaakStandWaarde = "werkt" | "te_laat" | "storing" | "onbekend";

export interface Achtergrondtaak {
  sleutel: string;
  naam: string;
  /** Wat de taak doet, in gewone taal. */
  wat: string;
  stand: TaakStandWaarde;
  ritme_minuten: number;
  laatste_goed_op: string | null;
  laatste_fout_op: string | null;
  /** Telling zonder klantgegevens ("12 in de wachtrij, ..."). */
  telling: string | null;
  /** Draait wel, maar doet niets: een korte zin, anders null. */
  let_op: string | null;
}

export interface Achtergrondtaken {
  taken: Achtergrondtaak[];
}

export const koppelingenApi = {
  async haal(): Promise<Koppelingen> {
    const { data } = await apiClient.get<Koppelingen>("/api/bewaking/koppelingen");
    return data;
  },
  async haalTaken(): Promise<Achtergrondtaken> {
    const { data } = await apiClient.get<Achtergrondtaken>("/api/bewaking/koppelingen/taken");
    return data;
  },
};

/**
 * Woord, symbool en kleurklasse per status. Het symbool is tekst, geen emoji.
 * Sessie 141 B1: de server zegt `uitgevallen`, het scherm zegt "storing"
 * (werkt, traag, storing: de woorden van JP).
 */
export const STATUS_WEERGAVE: Record<
  KoppelingStatus,
  { woord: string; symbool: string; kleur: string }
> = {
  werkt: { woord: "werkt", symbool: "✓", kleur: "blauw" },
  traag: { woord: "traag", symbool: "◐", kleur: "paars" },
  uitgevallen: { woord: "storing", symbool: "▲", kleur: "oranje" },
  onbekend: { woord: "onbekend", symbool: "?", kleur: "kp-grijs" },
};

/** De weergave van een status; een onbekende waarde van de server telt als onbekend. */
export function weergave(status: string) {
  return STATUS_WEERGAVE[status as KoppelingStatus] ?? STATUS_WEERGAVE.onbekend;
}

/** "9 werken, 1 traag, 1 storing, 1 onbekend". */
export function samenvattingTekst(s: KoppelingSamenvatting): string {
  const werkt = s.werkt ?? 0;
  return [
    werkt === 1 ? "1 werkt" : `${werkt} werken`,
    `${s.traag ?? 0} traag`,
    `${s.uitgevallen ?? 0} storing`,
    `${s.onbekend ?? 0} onbekend`,
  ].join(", ");
}

/** Woord, symbool en kleur per taakstand; zelfde symbolen als de koppelingen. */
export const TAAK_WEERGAVE: Record<
  TaakStandWaarde,
  { woord: string; symbool: string; kleur: string }
> = {
  werkt: { woord: "werkt", symbool: "✓", kleur: "blauw" },
  te_laat: { woord: "te laat", symbool: "◐", kleur: "paars" },
  storing: { woord: "storing", symbool: "▲", kleur: "oranje" },
  onbekend: { woord: "onbekend", symbool: "?", kleur: "kp-grijs" },
};

export function taakWeergave(stand: string) {
  return TAAK_WEERGAVE[stand as TaakStandWaarde] ?? TAAK_WEERGAVE.onbekend;
}

/** "zojuist", "3 min geleden", "2 uur geleden", "4 dagen geleden". */
export function geleden(moment: string, nu: number = Date.now()): string | null {
  const d = new Date(moment).getTime();
  if (Number.isNaN(d)) return null;
  const sec = Math.max(0, Math.round((nu - d) / 1000));
  if (sec < 60) return "zojuist";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min geleden`;
  const uur = Math.floor(min / 60);
  if (uur < 24) return `${uur} uur geleden`;
  const dagen = Math.floor(uur / 24);
  return dagen === 1 ? "1 dag geleden" : `${dagen} dagen geleden`;
}

/** "15:50"; langer dan een dag geleden met de dag erbij ("ma 05-10, 10:14"). */
export function tijdstip(moment: string, nu: number = Date.now()): string {
  const d = new Date(moment).getTime();
  if (Number.isNaN(d)) return "onbekend";
  return nu - d >= 24 * 3600 * 1000 ? dagTijd(moment) : klok(moment);
}

/** "traag, drempel 3000 ms": waarom een bron traag heet. */
export function drempelTekst(drempelMs: number): string {
  return `traag, drempel ${Math.round(drempelMs)} ms`;
}

/** "3 min geleden, 14:02"; ouder dan een dag met de dag erbij. */
export function laatsteContact(moment: string | null, nu: number = Date.now()): string {
  if (!moment) return "nog geen contact";
  const rel = geleden(moment, nu);
  if (rel === null) return "onbekend";
  const oud = nu - new Date(moment).getTime() >= 24 * 3600 * 1000;
  return `${rel}, ${oud ? dagTijd(moment) : klok(moment)}`;
}
